import React, { useCallback, useEffect, useMemo, useState } from 'react'
import StatusPicker from 'components/common/inputs/StatusPicker/StatusPicker'
import UpdateStatusModal from './Statuses/UpdateStatusModal'
import Actions from 'actions'
import useRolePermissions from 'hooks/useRolePermissions'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import usePlanLimits from 'hooks/usePlanLimits'
import usePracticeConfiguration from 'hooks/usePracticeConfiguration'
import { logError } from 'utils/logUtils'
import { hasUsableRetentionReference } from 'components/Patients/TreatmentTracker/trackingStampUtils'

const PatientStatusUpdate = ({
  id = '',
  patient,
  className = '',
  variant = 'rounded',
  analyticsSource,
  fullWidth = false
}) => {
  const { permissions } = useRolePermissions()
  const dispatch = useDispatch()
  const { isChurnedPlan } = usePlanLimits()

  const isSavingStatus = useSelector(state => state.treatmentReducer.statuses.isSaving)

  const [status, setStatus] = useState(null)
  const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false)

  const patientStatus = useMemo(() => patient?.treatments?.items[0]?.currentStatus?.status, [patient])

  const { ownerAppFeatureFlags } = usePracticeConfiguration()
  const practiceFFs = useSelector(state => state.multiPracticeReducer.practiceFFs)
  const hasRetentionTracker = ownerAppFeatureFlags?.treatmentTrackerRetention || practiceFFs?.treatmentTrackerRetention

  // The patient prop's treatment doesn't always select txTrackerStls; the treatment
  // reducer holds it for the currently open patient card.
  const reducerTreatment = useSelector(state => state.treatmentReducer.treatment)
  const hasActiveStls = useMemo(() => {
    const treatment = patient?.treatments?.items[0]
    const txTrackerStls =
      treatment?.txTrackerStls ?? (reducerTreatment?.id === treatment?.id ? reducerTreatment?.txTrackerStls : null)

    try {
      // The arch maps live under `stls`; the top level holds the provider and status.
      const parsedStls = JSON.parse(txTrackerStls || '{}')
      return !!(parsedStls.stls?.upper || parsedStls.stls?.lower)
    } catch (ex) {
      // Answering "no plan" here silently hides the reuse prompt from a patient who does
      // have one, and the doctor is given no other way back to it.
      logError('PatientStatusUpdate: could not read the treatment stl plan', { patientId: patient?.id, ex })
      return false
    }
  }, [patient, reducerTreatment])

  // A usable reference on the stack means the prompt already did its job: re-entering
  // Retention reuses the stack, and re-prompting would default to minting another
  // planned-final reference, splitting the scan history per reference set.
  const hasRetentionReference = useMemo(() => {
    const treatment = patient?.treatments?.items[0]
    const txTrackerRetentionStls =
      treatment?.txTrackerRetentionStls ??
      (reducerTreatment?.id === treatment?.id ? reducerTreatment?.txTrackerRetentionStls : null)
    return hasUsableRetentionReference(txTrackerRetentionStls)
  }, [patient, reducerTreatment])

  const showRetentionStlPrompt = !!hasRetentionTracker && hasActiveStls && !hasRetentionReference

  const didPatientDisableAccount = useMemo(() => {
    const currentTreatmentStatus = patient?.treatments?.items[0]?.currentStatus
    return (
      (currentTreatmentStatus?.status?.type === 'archived' || currentTreatmentStatus?.status?.type === 'transferred') &&
      currentTreatmentStatus?.setByUsername === patient?.username
    )
  }, [patient])

  const handleStatusSelection = useCallback(
    status => {
      if (!permissions.reactivatePatientAccount && didPatientDisableAccount) {
        return
      }

      setStatus(status)
      setIsUpdateStatusModalOpen(true)
    },
    [didPatientDisableAccount, permissions]
  )

  const cancelStatusChange = useCallback(() => {
    setStatus(patient.treatments?.items[0]?.currentStatus?.status)
    setIsUpdateStatusModalOpen(false)
  }, [patient])

  const handleStatusChangeSubmission = useCallback(
    ({
      cycleInterval,
      cycleIntervalPeriodType,
      treatmentType,
      totalAlignersSets,
      useLastStlForRetention,
      doubleScanEnabled
    }) => {
      trackEvent('Patient status update - status updated', {
        statusKey: status.key,
        cycleInterval,
        cycleIntervalPeriodType,
        analyticsSource,
        treatmentType,
        totalAlignersSets,
        useLastStlForRetention,
        doubleScanEnabled
      })
      // The planned-final reference is created by the status epic after the status
      // write settles; dispatching it here in parallel loses the status write to a
      // version conflict and wedges the patient on the old status.
      dispatch(
        Actions.updateTreatmentStatus({
          statusKey: status.key,
          treatmentId: patient.treatments?.items[0]?.id,
          cycleInterval,
          cycleIntervalPeriodType,
          treatmentType,
          totalAlignersSets,
          useLastStlForRetention,
          doubleScanEnabled,
          patientId: patient.id
        })
      )
    },
    [dispatch, patient, status, analyticsSource]
  )

  useEffect(() => {
    setStatus(patientStatus)
  }, [patientStatus])

  return (
    <>
      <StatusPicker
        id={id}
        program={patient?.user?.program}
        onSelect={handleStatusSelection}
        variant={variant}
        selectedStatus={status}
        fullWidth={fullWidth}
        disabled={
          isChurnedPlan ||
          !permissions.editPatientScanSettings ||
          (!permissions.reactivatePatientAccount && didPatientDisableAccount)
        }
        className={className}
      />
      <UpdateStatusModal
        isOpen={isUpdateStatusModalOpen}
        selectedStatus={status}
        onCancel={cancelStatusChange}
        onSubmit={handleStatusChangeSubmission}
        onClose={() => setIsUpdateStatusModalOpen(false)}
        isLoading={isSavingStatus}
        showRetentionStlPrompt={showRetentionStlPrompt}
        currentDoubleScanEnabled={!!patient.treatments?.items[0]?.doubleScanEnabled}
        currentTreatmentType={patient.treatments?.items[0]?.type}
      />
    </>
  )
}

export default PatientStatusUpdate
